// Monitoring services - Sentry removed, using Elasticsearch for error tracking
import { performanceService } from './performanceService';
import { analyticsService } from './analyticsService';
import { loggingService, LogLevel } from './loggingService';

interface MonitoringConfig {
  // Removed Sentry configuration - using Elasticsearch instead
  performance?: {
    enableConsoleLogging?: boolean;
    enableAnalytics?: boolean;
  };
  analytics?: {
    enableConsoleLogging?: boolean;
    enableLocalStorage?: boolean;
    batchSize?: number;
    flushInterval?: number;
    customEndpoint?: string;
  };
  logging?: {
    level?: LogLevel;
    enableConsole?: boolean;
    enableAnalytics?: boolean;
    enableLocalStorage?: boolean;
    maxLocalStorageEntries?: number;
    enableRemoteLogging?: boolean;
    remoteEndpoint?: string;
  };
}

class MonitoringService {
  private initialized = false;

  initialize(config: MonitoringConfig): void {
    if (this.initialized) {
      console.warn('Monitoring service already initialized');
      return;
    }

    // Initialize logging service
    if (config.logging) {
      loggingService.initialize(config.logging);
    }

    // Initialize performance monitoring
    if (config.performance) {
      performanceService.initialize(config.performance);
    }

    // Initialize analytics
    if (config.analytics) {
      analyticsService.initialize(config.analytics);
    }

    this.initialized = true;
    
    // Log successful initialization
    loggingService.info('Monitoring services initialized', {
      services: {
        elasticsearch: true, // Using Elasticsearch for error tracking
        performance: !!config.performance,
        analytics: !!config.analytics,
        logging: !!config.logging,
      },
    }, 'MonitoringService', 'initialize');
  }

  // Convenience methods that delegate to appropriate services
  trackEvent(event: string, properties?: Record<string, any>): void {
    analyticsService.track(event, properties);
  }

  trackFeatureUsage(feature: string, action: string, properties?: Record<string, any>): void {
    analyticsService.trackFeatureUsage(feature, action, properties);
  }

  trackError(error: Error, context?: Record<string, any>): void {
    loggingService.error(error.message, {
      errorName: error.name,
      errorStack: error.stack,
      ...context,
    }, 'ErrorHandler', 'trackError');
    
    analyticsService.trackError(error, context);
    // Sentry removed - errors now go to Elasticsearch via logging service
  }

  trackPerformance(name: string, value: number, metadata?: Record<string, any>): void {
    performanceService.trackCustomMetric(name, value, metadata);
  }

  log = {
    trace: (message: string, context?: Record<string, any>, component?: string, action?: string) => 
      loggingService.trace(message, context, component, action),
    debug: (message: string, context?: Record<string, any>, component?: string, action?: string) => 
      loggingService.debug(message, context, component, action),
    info: (message: string, context?: Record<string, any>, component?: string, action?: string) => 
      loggingService.info(message, context, component, action),
    warn: (message: string, context?: Record<string, any>, component?: string, action?: string) => 
      loggingService.warn(message, context, component, action),
    error: (message: string, context?: Record<string, any>, component?: string, action?: string) => 
      loggingService.error(message, context, component, action),
    fatal: (message: string, context?: Record<string, any>, component?: string, action?: string) => 
      loggingService.fatal(message, context, component, action),
  };

  setUser(user: { id: string; email?: string; username?: string }): void {
    // Sentry setUser removed - using Elasticsearch for user tracking
    analyticsService.identify(user.id, {
      email: user.email,
      username: user.username,
    });
  }

  flush(): void {
    analyticsService.flush();
  }

  getPerformanceSummary(): Record<string, any> {
    return performanceService.getPerformanceSummary();
  }

  exportLogs(format: 'json' | 'csv' = 'json'): string {
    return loggingService.exportLogs(format);
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}

export const monitoringService = new MonitoringService();

// Export individual services for direct access if needed
export {
  performanceService,
  analyticsService,
  loggingService,
  LogLevel,
};

// Export types
export type { MonitoringConfig };